import React, { useCallback, useEffect, useRef, useState } from 'react';
import { EnvelopeSimple, CheckCircle } from '@phosphor-icons/react';
import { DillModal } from '../../../../../general/Dill/DillModal';
import { DillButton } from '../../../../../general/Dill/DillButton';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/store';
import { createCollectionActivity } from '../../../../../../redux/services/collectionActivityService';
import { createPromiseToPay } from '../../../../../../redux/services/promiseToPayService';
import { AppInputField } from '../../../../../general/AppInputField/AppInputField';
import { openMessageModal } from '../../../../../../redux/globalSlices/genericSlice';
import { BuyerContact, CollectionActivityReference } from '@dill/dill-shared';
import ActivityReferenceSelector from '../../ActivityReferenceSelector/ActivityReferenceSelector';
import {
  PromiseToPayFieldsSection,
  defaultPromiseToPayFieldsValue,
  isPromiseToPayFieldsValid,
  PromiseToPayFieldsValue,
} from '../PromiseToPayFieldsSection/PromiseToPayFieldsSection';

interface AddEmailModalProps {
  open: boolean;
  buyerId: string;
  emailAddress?: string;
  sendingMode?: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  initialReferences?: CollectionActivityReference[];
  contacts?: BuyerContact[];
}

type SendingStage = 'FORM' | 'SENDING' | 'DELIVERED';

// Demo-only: simulates sending an email (no real email is sent), then logs it as a manual
// PAYMENT_REMINDER activity with channel: 'EMAIL' - that's already the shape used for a genuine
// emailed reminder (recipientEmail/emailSubject/emailBody), it's just source: 'MANUAL' here rather
// than an automated one, and already shows up correctly under the "Emails" filter tab.
function AddEmailModal({
  open,
  buyerId,
  emailAddress = '',
  sendingMode = false,
  onClose,
  onSuccess,
  initialReferences,
  contacts,
}: AddEmailModalProps) {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { loadingList } = useAppSelector((state) => state.collectionActivities);

  const [email, setEmail] = useState(emailAddress);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [references, setReferences] = useState<CollectionActivityReference[]>(
    initialReferences ?? []
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [subjectError, setSubjectError] = useState('');
  const [bodyError, setBodyError] = useState('');
  const [promiseFields, setPromiseFields] = useState<PromiseToPayFieldsValue>(
    defaultPromiseToPayFieldsValue
  );

  // Sending mode state
  const [sendingStage, setSendingStage] = useState<SendingStage>('FORM');
  const [showFullForm, setShowFullForm] = useState(!sendingMode);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isLoading = loadingList.includes('createCollectionActivity');

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setEmail(emailAddress);
      setSubject('');
      setBody('');
      setReferences([]);
      setEmailError('');
      setSubjectError('');
      setBodyError('');
      setIsSubmitting(false);
      setSendingStage('FORM');
      setShowFullForm(!sendingMode);
      setPromiseFields(defaultPromiseToPayFieldsValue);
    }
  }, [open, emailAddress, sendingMode]);

  useEffect(() => {
    if (open && initialReferences) {
      setReferences(initialReferences);
    }
  }, [open, initialReferences]);

  // Cleanup timeout on unmount or close
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  const validateForm = useCallback(() => {
    let isValid = true;
    if (!email.trim()) {
      setEmailError('Please enter an email address');
      isValid = false;
    }
    if (!subject.trim()) {
      setSubjectError('Please enter a subject');
      isValid = false;
    }
    if (!body.trim()) {
      setBodyError('Please enter the email body');
      isValid = false;
    }
    return isValid;
  }, [email, subject, body]);

  const buildActivityData = () => ({
    supplierId: currentSupplier?.id || '',
    buyerId,
    activityType: 'PAYMENT_REMINDER' as const,
    source: 'MANUAL' as const,
    performedBy: user!.id,
    date: new Date(),
    reminderDetails: {
      invoiceIds: [],
      reminderType: 'PAYMENT_REMINDER' as const,
      channel: 'EMAIL' as const,
      recipientEmail: email,
      emailSubject: subject,
      emailBody: body,
      action: 'SENT' as const,
    },
    references: references.length > 0 ? references : undefined,
  });

  const handleSend = async () => {
    if (!validateForm()) return;

    // Show sending animation
    setSendingStage('SENDING');

    // Simulate sending delay, then show delivered, then open log form
    setTimeout(() => {
      setSendingStage('DELIVERED');
      closeTimeoutRef.current = setTimeout(() => {
        setShowFullForm(true);
        setSendingStage('FORM');
      }, 1200);
    }, 1500);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    if (!currentSupplier?.id) {
      return;
    }

    if (!isPromiseToPayFieldsValid(promiseFields)) {
      dispatch(
        openMessageModal({
          title: 'Missing promise details',
          subTitle:
            'Please fill in the payment type, reason, and promised date, or uncheck "Customer promised to pay".',
          buttonText: 'OK',
          modalType: 'ERROR',
        })
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await dispatch(
        createCollectionActivity({
          supplierId: currentSupplier.id,
          activityData: buildActivityData(),
        })
      );

      if (result.payload && (result.payload as any).success) {
        if (promiseFields.logPromise) {
          try {
            await dispatch(
              createPromiseToPay({
                supplierId: currentSupplier.id,
                data: {
                  supplierId: currentSupplier.id,
                  buyerId,
                  source: 'EMAIL',
                  type: promiseFields.type || null,
                  reason: promiseFields.reason || null,
                  reasonOther:
                    promiseFields.reason === 'OTHER' ? promiseFields.reasonOther || null : null,
                  promisedPaymentDate: new Date(promiseFields.promisedPaymentDate),
                  amount: promiseFields.amount ? parseFloat(promiseFields.amount) : null,
                  references: references.length > 0 ? references : null,
                  notes: null,
                  status: 'ACTIVE',
                },
              })
            );
          } catch (promiseError) {
            console.error('Failed to log promise to pay:', promiseError);
            dispatch(
              openMessageModal({
                title: 'Email logged, but promise to pay failed',
                subTitle:
                  'The email was logged successfully, but we could not save the promise to pay. Please add it manually.',
                buttonText: 'OK',
                modalType: 'ERROR',
              })
            );
          }
        }
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      console.error('Failed to log email:', error);
      dispatch(
        openMessageModal({
          title: 'Error',
          subTitle: 'Failed to log email. Please try again.',
          buttonText: 'OK',
          modalType: 'ERROR',
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isLoading) return;
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setEmail('');
    setSubject('');
    setBody('');
    setReferences([]);
    setEmailError('');
    setSubjectError('');
    setBodyError('');
    setPromiseFields(defaultPromiseToPayFieldsValue);
    onClose();
  };

  const isAnimating = sendingStage === 'SENDING' || sendingStage === 'DELIVERED';

  return (
    <DillModal
      open={open}
      onClose={handleClose}
      title={showFullForm ? 'Log Email' : 'Send Email'}
      maxWidth="576px"
      closeOnOverlayClick={!isAnimating}
      footer={
        !isAnimating ? (
          <>
            <DillButton
              onClick={handleClose}
              variant="secondary"
              isDisabled={isLoading}
              label="Cancel"
            />
            <DillButton
              onClick={showFullForm ? handleSubmit : handleSend}
              variant="primary"
              isLoading={isLoading || isSubmitting}
              label={showFullForm ? 'Log Email' : 'Send'}
            />
          </>
        ) : undefined
      }>
      {/* SENDING stage */}
      {sendingStage === 'SENDING' && (
        <div className="flex flex-col items-center py-8">
          <div className="relative flex items-center justify-center w-24 h-24 mb-6">
            <div className="add-call-pulse-ring absolute inset-0 rounded-full bg-amber-400" />
            <div className="add-call-pulse-ring add-call-pulse-ring-delayed absolute inset-0 rounded-full bg-amber-400" />
            <div className="relative z-10 w-16 h-16 rounded-full bg-amber-500 flex items-center justify-center">
              <EnvelopeSimple size={28} weight="fill" className="text-white" />
            </div>
          </div>
          <p className="text-lg font-semibold text-gray-800 mb-1">Sending...</p>
          {email && <p className="text-sm text-gray-500 mb-2">{email}</p>}
          <p className="text-xs text-gray-400 max-w-[240px] text-center mt-2 truncate">{subject}</p>
        </div>
      )}

      {/* DELIVERED stage */}
      {sendingStage === 'DELIVERED' && (
        <div className="flex flex-col items-center py-8">
          <div className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center mb-4">
            <CheckCircle size={28} weight="fill" className="text-white" />
          </div>
          <p className="text-lg font-semibold text-emerald-600 mb-1">Email Sent</p>
          {email && <p className="text-sm text-gray-500">{email}</p>}
        </div>
      )}

      {/* FORM stage */}
      {sendingStage === 'FORM' && (
        <div className="space-y-4">
          <AppInputField
            label="Email Address"
            isRequired
            id="email-address"
            value={email}
            onTextChange={(val) => {
              setEmail(val);
              if (emailError) setEmailError('');
            }}
            placeholder="name@company.com"
            isDisabled={isLoading}
            errorText={emailError}
          />

          <AppInputField
            label="Subject"
            isRequired
            id="email-subject"
            value={subject}
            onTextChange={(val) => {
              setSubject(val);
              if (subjectError) setSubjectError('');
            }}
            placeholder="e.g. Reminder: Invoice #1234 is past due"
            isDisabled={isLoading}
            errorText={subjectError}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Body <span className="text-red-500">*</span>
            </label>
            <textarea
              value={body}
              onChange={(e) => {
                setBody(e.target.value);
                if (bodyError) setBodyError('');
              }}
              placeholder="Enter the email body..."
              rows={5}
              disabled={isLoading}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-50 focus:border-blue-400 transition-all resize-y"
            />
            {bodyError && <p className="mt-1 text-xs text-red-500">{bodyError}</p>}
          </div>

          {showFullForm && (
            <ActivityReferenceSelector
              label="References (Optional)"
              buyerId={buyerId}
              value={references}
              onChange={setReferences}
              isDisabled={isLoading}
              contacts={contacts}
            />
          )}

          {showFullForm && (
            <PromiseToPayFieldsSection
              value={promiseFields}
              onChange={setPromiseFields}
              isDisabled={isLoading}
            />
          )}
        </div>
      )}
    </DillModal>
  );
}

export default AddEmailModal;
